import React, { useState } from 'react';
import { ChevronLeft, Heart, ChevronDown, MessageCircle } from 'lucide-react';
import IngredientGlossarySheet from './IngredientGlossarySheet';
import { glossaryEntryFor, glossarySegments } from '../utils/ingredientGlossary';

const INK = '#2A324B';

// 2026-06-09: per Sofia feedback, the Match Score is the ONLY inner card kept.
// All other sections render as full-width page content — no border, no shadow,
// no card-in-card boxing. Reduces the "everything is a box" feel and lets the
// content read more like Skin Analysis.
const CARD = 'bg-white rounded-2xl border border-[#341C1C]/8 shadow-sm';

const KEY_INGREDIENT_PREVIEW = 6;

const band = (s) =>
  s >= 75 ? { t: 'Good', c: '#3B7A3B', bg: '#F0F7F0' }
  : s >= 55 ? { t: 'Fair', c: '#B26B00', bg: '#FFF4E5' }
  : { t: 'Low', c: '#C0392B', bg: '#FEECEC' };

const Chevron = ({ open }) => (
  <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(42,50,75,0.45)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
);

const ProductAnalysisRedesign = ({ result, onBack, onAnalyseAnother, onAsk, onSave, onSaveRequest, glossaryPreview = true }) => {
  const [saved, setSaved] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(false);
  const [formulaOpen, setFormulaOpen] = useState(true);  // open by default — the ingredient breakdown is core content, not a hidden extra
  const [usageOpen, setUsageOpen] = useState(false);
  const [ingExpanded, setIngExpanded] = useState(false);
  const [fullInciOpen, setFullInciOpen] = useState(false);
  const [glossaryEntry, setGlossaryEntry] = useState(null);

  const p = result.product || {};
  const ing = p.keyIngredients || [];
  const formula = p.formulation_bullets || [];
  const fullInci = typeof p.ingredientsFull === 'string' ? p.ingredientsFull.trim() : '';
  const score = result.matchScore || 0;
  const basis = result.personalizationBasis || 'saved_profile';
  const isFormulaOnly = basis === 'formula_only';
  const usesQuickAnswers = basis === 'quick_answers';
  const pros = result.analysis?.pros || [];
  const cons = result.analysis?.cons || [];
  const usage = result.recommendation?.usage;

  const radius = 34, circ = 2 * Math.PI * radius, offset = circ - (score / 100) * circ;
  const scoreLabel = isFormulaOnly
    ? (score >= 75 ? 'Strong formula based on what we could verify.' : score >= 55 ? 'Mixed formula based on what we could verify.' : 'Limited formula based on what we could verify.')
    : usesQuickAnswers
      ? (score >= 85 ? 'Excellent fit based on what you told us for this result.' : score >= 70 ? 'Good fit based on what you told us for this result.' : score >= 50 ? 'Moderate fit based on what you told us for this result.' : 'Limited fit based on what you told us for this result.')
      : score >= 85 ? 'Excellent compatibility with your saved skin profile.'
      : score >= 70 ? 'Good compatibility with your saved skin profile.'
      : score >= 50 ? 'Moderate compatibility with your saved skin profile.'
      : 'Limited compatibility with your saved skin profile.';

  const dims = result.scoring
    ? Object.entries(result.scoring).filter(([, d]) => d && d.assessed && typeof d.score === 'number')
    : [];
  const DIM_LABELS = {
    concerns: 'Your concerns', skin_type: 'Skin type', ingredient_safety: 'Ingredient safety',
    formulation: 'Formulation', shade_match: 'Shade match', texture_finish: 'Texture & finish',
  };

  return (
    <div className="flex flex-col h-full" style={{ background: '#FFFFFF' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-3 flex-shrink-0">
        <button onClick={onBack} className="p-1.5 rounded-xl hover:bg-black/5">
          <ChevronLeft className="h-5 w-5" style={{ color: INK }} />
        </button>
        <div>
          <h2 className="text-lg font-bold leading-tight" style={{ color: INK }}>Product Analysis</h2>
          <p className="text-xs" style={{ color: 'rgba(42,50,75,0.45)' }}>{isFormulaOnly ? 'Formula and ingredient assessment' : usesQuickAnswers ? 'Based only on what you told us for this result' : 'Compatibility with your saved skin profile'}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-5" style={{ background: '#FFFFFF' }}>
        {/* Product hero — borderless, content fills page width.
            Hero image is now LARGER (24x24 vs 14x14) when an uploaded photo is
            available so the user's actual product is visible, not a tiny chip. */}
        <div className="pt-3 pb-1 flex gap-4 items-start">
          <div className={`${p.image ? 'w-24 h-24' : 'w-14 h-14'} rounded-2xl overflow-hidden flex-shrink-0 bg-[#F3D5B5]/20 flex items-center justify-center`}>
            {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <span className="text-xl">🧴</span>}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[16px] leading-snug" style={{ color: INK }}>
              {/^\s*(unknown product|unable to read.*|error)\s*$/i.test(p.name || '') ? 'Unnamed product' : p.name}
            </h3>
            <p className="text-xs mb-2" style={{ color: 'rgba(42,50,75,0.55)' }}>
              {/^\s*(unknown product|unable to read.*|error)\s*$/i.test(p.name || '')
                ? "We couldn't read the label — your analysis is below"
                : p.brand}
            </p>
            {p.category && (
              <span className="inline-block px-2.5 py-0.5 rounded-full text-[13px] font-medium border" style={{ borderColor: 'rgba(42,50,75,0.15)', color: 'rgba(42,50,75,0.7)' }}>{p.category}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              // Defer to parent's list-picker modal when provided (Sofia: the
              // old "which list?" picker must come back instead of going
              // straight to wishlist). Fall back to onSave for legacy callers.
              if (onSaveRequest) { onSaveRequest(); return; }
              setSaved(s => !s);
              onSave && onSave();
            }}
            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: saved ? INK : 'rgba(42,50,75,0.06)' }}
            aria-label={saved ? 'Saved' : 'Save'}
          >
            <Heart className="w-4 h-4" style={{ color: saved ? '#fff' : INK }} fill={saved ? '#fff' : 'none'} />
          </button>
        </div>

        {/* Match score — the ONLY inner card kept (Sofia: keep this one).
            2026-06-09: header is now a single bigger statement ("Good
            compatibility with your skin profile") instead of stacking
            "Your Match Score" over a separate label line; "Powered by Son:mi"
            stepped up in weight + colour so the attribution reads more
            confidently. The score percentage stays in the ring. */}
        <div className={`${CARD} overflow-hidden`}>
          <div className="p-5 flex items-center gap-5" style={{ background: INK }}>
            <div className="relative flex-shrink-0">
              <svg width="92" height="92" className="-rotate-90" aria-hidden>
                <circle cx="46" cy="46" r={radius} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="5" />
                <circle cx="46" cy="46" r={radius} fill="none" stroke="#F3D5B5" strokeWidth="5" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset .8s ease' }} />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-white">{score}<span className="text-xs font-semibold text-white/70">%</span></span>
              </div>
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-white text-[16px] leading-snug">{scoreLabel}</h4>
              <p className="text-[#F3D5B5] text-[13px] font-semibold mt-2 tracking-[0.08em]">Powered by Son:mi</p>
            </div>
          </div>
          {dims.length > 0 && (
            <>
              <button type="button" onClick={() => setScoreOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3.5 text-left">
                <span className="text-[15px] font-semibold" style={{ color: INK }}>{isFormulaOnly ? 'How we assessed the formula' : 'How we scored it'}</span>
                <Chevron open={scoreOpen} />
              </button>
              {scoreOpen && (
                <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid rgba(52,28,28,0.06)', paddingTop: 12 }}>
                  <p className="text-[13px]" style={{ color: 'rgba(42,50,75,0.45)' }}>{isFormulaOnly ? 'This assessment uses only the product information we could verify.' : 'Your match score is the weighted average of these.'}</p>
                  {dims.map(([key, d]) => {
                    const b = band(d.score);
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[15px]" style={{ color: INK }}>{DIM_LABELS[key] || key}</span>
                          <span className="text-[13px] font-semibold px-2.5 py-0.5 rounded-full" style={{ color: b.c, background: b.bg }}>{b.t} · {d.score}</span>
                        </div>
                        {d.reason && <p className="text-[13px] mt-0.5 leading-snug" style={{ color: 'rgba(42,50,75,0.5)' }}>{d.reason}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Key Ingredients — borderless, collapsible past 6 chips */}
        {ing.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-[13px] font-semibold tracking-[0.16em] uppercase" style={{ color: 'rgba(42,50,75,0.4)' }}>Key Ingredients</p>
              {glossaryPreview && <span className="text-[12px] text-[#2A324B]/55">Tap to learn</span>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(ingExpanded ? ing : ing.slice(0, KEY_INGREDIENT_PREVIEW)).map((x, i) => {
                const entryId = glossaryPreview ? glossaryEntryFor(x) : null;
                return entryId ? (
                  <button key={i} type="button" onClick={() => setGlossaryEntry(entryId)} className="min-h-10 rounded-full border border-[#2A324B]/30 bg-[#F6F7FA] px-3 text-[13px] font-medium text-[#2A324B] underline decoration-dotted underline-offset-4">{x}</button>
                ) : (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-white border" style={{ borderColor: 'rgba(42,50,75,0.15)', color: INK }}>{x}</span>
                );
              })}
              {!ingExpanded && ing.length > KEY_INGREDIENT_PREVIEW && (
                <button
                  type="button"
                  onClick={() => setIngExpanded(true)}
                  className="text-xs px-2.5 py-1 rounded-full"
                  style={{ color: 'rgba(42,50,75,0.65)', background: 'rgba(42,50,75,0.06)' }}
                >
                  +{ing.length - KEY_INGREDIENT_PREVIEW} more
                </button>
              )}
            </div>
          </div>
        )}

        {/* Understanding the formulation — open by default. Ingredient-focused
            bullets ONLY (each active + what it IS/DOES + INCI position): pure
            formulation science, matching Compare. Routine fit lives in "What this
            means for you". No routine-prose fallback — that ingredient_insight
            write-up was redundant with pros/cons (Sofia). */}
        {formula.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setFormulaOpen(o => !o)}
              className="w-full flex items-center justify-between py-3 text-left"
              style={{ borderTop: '1px solid rgba(52,28,28,0.08)' }}
            >
              <span className="text-[15px] font-semibold" style={{ color: INK }}>Understanding the formulation</span>
              <Chevron open={formulaOpen} />
            </button>
            {formulaOpen && (
              <ul className="pt-1 pb-2 space-y-2">
                {formula.map((b, i) => (
                  <li key={i} className="text-[15px] flex gap-2 leading-relaxed" style={{ color: 'rgba(42,50,75,0.78)' }}>
                    <span className="text-[#C8956A] mt-0.5 flex-shrink-0">•</span>
                    <span>{glossaryPreview && glossaryEntryFor(b.ingredient) ? (
                      <button type="button" onClick={() => setGlossaryEntry(glossaryEntryFor(b.ingredient))} className="font-semibold text-[#2A324B] underline decoration-dotted underline-offset-4">{b.ingredient}</button>
                    ) : <span className="font-semibold" style={{ color: INK }}>{b.ingredient}</span>}{b.note ? ` — ${b.note}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Complete verified formula follows the readable highlights. It stays
            collapsed on a phone, but is always available before the verdict. */}
        {fullInci && (
          <div>
            <button
              type="button"
              onClick={() => setFullInciOpen((open) => !open)}
              aria-expanded={fullInciOpen}
              className="w-full min-h-11 flex items-center justify-between gap-3 py-3 text-left"
              style={{ borderTop: '1px solid rgba(52,28,28,0.08)', color: INK }}
            >
              <span className="text-[15px] font-semibold">{fullInciOpen ? 'Hide full ingredient list' : 'View full ingredient list'}</span>
              <Chevron open={fullInciOpen} />
            </button>
            {fullInciOpen && (
              <div className="pb-3">
                <p className="text-[13px] mb-2" style={{ color: 'rgba(42,50,75,0.55)' }}>Complete INCI from the product catalogue, in label order.</p>
                <p className="text-[14px] leading-7 break-words" style={{ color: 'rgba(42,50,75,0.82)' }}>
                  {glossaryPreview ? glossarySegments(fullInci).map((segment, index) => segment.id ? (
                    <button key={index} type="button" onClick={() => setGlossaryEntry(segment.id)} className="inline text-[#2A324B] underline decoration-dotted underline-offset-4">{segment.text}</button>
                  ) : <React.Fragment key={index}>{segment.text}</React.Fragment>) : fullInci}
                </p>
              </div>
            )}
          </div>
        )}

        {/* What this means for you — borderless, full-width */}
        {(pros.length > 0 || cons.length > 0) && (
          <div>
            <h4 className="text-[15px] font-semibold mb-3" style={{ color: INK }}>{isFormulaOnly ? 'What the formula suggests' : 'What this means for you'}</h4>
            <div className="space-y-2.5">
              {pros.map((item, i) => (
                <div key={`p${i}`} className="flex items-start gap-3">
                  <span className="mt-0.5 w-4 h-4 rounded-full border-2 border-green-400 flex items-center justify-center flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  </span>
                  <span className="text-[15px] leading-snug" style={{ color: 'rgba(42,50,75,0.8)' }}>{item}</span>
                </div>
              ))}
              {cons.map((item, i) => (
                <div key={`c${i}`} className="flex items-start gap-3">
                  <span className="mt-0.5 w-4 h-4 rounded-full border-2 border-amber-400 flex items-center justify-center flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  </span>
                  <span className="text-[15px] leading-snug" style={{ color: 'rgba(42,50,75,0.8)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How to use it — borderless expander row */}
        {usage && (
          <div>
            <button
              type="button"
              onClick={() => setUsageOpen(o => !o)}
              className="w-full flex items-center justify-between py-3 text-left"
              style={{ borderTop: '1px solid rgba(52,28,28,0.08)' }}
            >
              <span className="text-[15px] font-semibold" style={{ color: INK }}>How to use it</span>
              <Chevron open={usageOpen} />
            </button>
            {usageOpen && (
              <p className="pt-1 pb-2 text-[15px] leading-relaxed" style={{ color: 'rgba(42,50,75,0.72)' }}>{usage}</p>
            )}
          </div>
        )}
      </div>
      {glossaryPreview && <IngredientGlossarySheet entryId={glossaryEntry} onClose={() => setGlossaryEntry(null)} />}

      {/* Bottom actions */}
      <div className="flex gap-3 px-4 py-3.5 flex-shrink-0" style={{ borderTop: '1px solid rgba(52,28,28,0.08)', background: '#fff' }}>
        <button onClick={onAnalyseAnother} className="flex-1 py-3 rounded-full text-sm font-semibold" style={{ border: '1.5px solid rgba(42,50,75,0.18)', background: '#fff', color: INK }}>
          Analyse another
        </button>
        <button onClick={onAsk} className="flex-1 py-3 rounded-full text-sm font-semibold text-white flex items-center justify-center gap-1.5" style={{ background: INK }}>
          <MessageCircle className="w-4 h-4" /> Ask a question
        </button>
      </div>
    </div>
  );
};

export default ProductAnalysisRedesign;
